import styles from './SegmentedToggle.module.scss'

interface SegmentedOption<T extends string> {
    value: T
    label: string
}

interface SegmentedToggleProps<T extends string> {
    label?: string
    value: T
    options: SegmentedOption<T>[]
    onChange: (value: T) => void
    required?: boolean
}

/**
 * Full-width toggle between equal-width options, with a highlight that slides
 * to the selected one. Arrow keys move between options.
 */
export const SegmentedToggle = <T extends string>({ label, value, options, onChange, required = false }: SegmentedToggleProps<T>) => {
    const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value))

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
        e.preventDefault()
        const step = e.key === 'ArrowRight' ? 1 : -1
        onChange(options[(selectedIndex + step + options.length) % options.length].value)
    }

    return (
        <div className={styles.field}>
            {label && (
                <span className={styles.label}>
                    {label}
                    {required && <span className={styles.req}>*</span>}
                </span>
            )}
            <div className={styles.track} role="radiogroup" aria-label={label} onKeyDown={onKeyDown}>
                <span
                    className={styles.thumb}
                    aria-hidden="true"
                    // Track has 3px padding each side; translateX(100%) moves by the thumb's own width
                    style={{ width: `calc((100% - 6px) / ${options.length})`, transform: `translateX(${selectedIndex * 100}%)` }}
                />
                {options.map((opt) => {
                    const selected = opt.value === value
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            tabIndex={selected ? 0 : -1}
                            className={`${styles.option} ${selected ? styles.selected : ''}`}
                            onClick={() => onChange(opt.value)}
                        >
                            {opt.label}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
